import React from 'react';
import {Button, Card, Divider, Tooltip} from 'antd';
import {HeartFilled, HeartOutlined} from '@ant-design/icons';
import {ModelDetailDTO} from 'hemwb-api';
import {ModelStatusTag, tagType} from '../list/ModelStatusTag';
import {InformationClassificationIcon} from '../../../icons/classification/InformationClassificationIcon';
import {GlobalAdaptationCell} from '../list/GlobalAdaptationCell';
import {isModelExternal, isModelWeb} from '../../../../store/model';
import {fullNameWestern, convertCommaSeparatedNameToWestern} from '../../../../store/user/utils';
import {getModelLanguageAndExecutionEnvironment, addSpaceAfterSlash} from '../common/ModelTextOverview/utils';
import ModelOverviewRequestAccess from '../common/ModelOverview/ModelOverviewRequestAccess';
import {OperationCell} from '../list/ModelItemActionMenu';
import {isModelTheorem, getModelGlobalAsTextValue} from '../../../../store/model';
import {fields as metadataFieldLabels, metadataLabels} from '../common/ModelEdit/ModelEditMetadata/constants';
import {useToggleFavorite} from '../../../../store/favorite';
import styles from './ModelDetailCard.module.scss';

interface ModelDetailCardProps {
  model: ModelDetailDTO;
  isExternalUser: boolean;
}

export const ModelDetailCard: React.FC<ModelDetailCardProps> = ({model, isExternalUser}) => {
  const externalModel = isModelExternal(model);
  const webModel = isModelWeb(model);
  const toggleFavorite = useToggleFavorite();
  const isFav = !!model.favorite;
  const mdpLink = model.metadata?.model_development_plan;
  const hasMdpLink = !!mdpLink && mdpLink.trim() !== '' && mdpLink.trim().toLowerCase() !== 'null';

  return (
    <Card
      data-tour-id="model-detail-card"
      className={styles.modelDetailCard}
      title={
        <div className={styles.cardTitle}>
          <span className={styles.modelName} title={model.name}>
            {model.name}
          </span>
        </div>
      }
      extra={
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          {isExternalUser ? null : (
            <Tooltip title={isFav ? 'Remove from favorites' : 'Add to favorites'}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  lineHeight: 1,
                  width: '28px',
                  height: '28px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px',
                  background: '#fff',
                }}
                onClick={() => toggleFavorite(model.uuid, isFav)}
              >
                {isFav ? (
                  <HeartFilled style={{color: '#00857c', fontSize: '16px'}} />
                ) : (
                  <HeartOutlined style={{color: '#bfbfbf', fontSize: '16px'}} />
                )}
              </span>
            </Tooltip>
          )}
          <OperationCell model={model} buttonType="default" placement="bottomLeft" tourId="model-detail-action-menu" />
        </div>
      }
    >
      {/* Classification and Status */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['information_classification']}:</span>
          <span className={styles.value}>
            <InformationClassificationIcon
              classification={model.metadata.information_classification}
              markOnly={false}
              className={styles.classification}
            />
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.label}>Status:</span>
          <span className={styles.value}>
            <ModelStatusTag status={model?.metadata?.status?.toLowerCase() as tagType} />
          </span>
        </div>
      </div>

      <Divider className={styles.divider} />

      {/* Model Details Grid */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailRow}>
          <span className={styles.label}>Global/Adaptation:</span>
          <span className={styles.value}>{getModelGlobalAsTextValue(model)}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['country']}:</span>
          <span className={styles.value}>
            <GlobalAdaptationCell model={model} flagOnly displayCountry />
          </span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>Version:</span>
          <span className={styles.value}>
            {model.metadata.version_release_of_model ? `Ver. ${model.metadata.version_release_of_model}` : '-'}
          </span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['model_development_plan']}:</span>
          <span className={styles.value}>
            {hasMdpLink ? (
              <Button type="link" size="small">
                <a href={mdpLink} target="_blank" rel="noopener noreferrer">
                  LINK
                </a>
              </Button>
            ) : (
              '-'
            )}
          </span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['stage_of_model_development']}:</span>
          <span className={styles.value}>{addSpaceAfterSlash(model.metadata.stage_of_model_development) || '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['version_log']}:</span>
          <span className={styles.value}>{model.metadata.version_log || '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['objective_of_model']}:</span>
          <span className={styles.value}>{model.metadata.objective_of_model || '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['model_type']}:</span>
          <span className={styles.value}>{model.metadata.model_type || '-'}</span>
        </div>
      </div>

      <Divider className={styles.divider} />

      {/* Therapeutic Details */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailRow}>
          <span className={styles.label}>TA:</span>
          <span className={styles.value}>{model.metadata.therapeutic_area || '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['disease']}:</span>
          <span className={styles.value}>{model.metadata.disease || '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>Drug/Product Name:</span>
          <span className={styles.value}>{model.metadata.product || '-'}</span>
        </div>

        {externalModel && (
          <>
            <div className={styles.detailRow}>
              <span className={styles.label}>MK#:</span>
              <span className={styles.value}>{model.metadata.mk_number || '-'}</span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.label}>{metadataFieldLabels['indication']}:</span>
              <span className={styles.value}>{model.metadata.indication || '-'}</span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.label}>{metadataFieldLabels['study_number']}:</span>
              <span className={styles.value}>{model.metadata.study_number || '-'}</span>
            </div>
          </>
        )}
      </div>

      <Divider className={styles.divider} />

      {/* Team Details */}
      <div className={styles.detailsGrid}>
        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataLabels['modelSteward']}:</span>
          <span className={styles.value}>
            {model.metadata.name_of_model_steward
              ? convertCommaSeparatedNameToWestern(model.metadata.name_of_model_steward)
              : '-'}
          </span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>V&I lead:</span>
          <span className={styles.value}>{model.vestLead ? fullNameWestern(model.vestLead) : '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>Creator Name:</span>
          <span className={styles.value}>{model.createdBy ? fullNameWestern(model.createdBy) : '-'}</span>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.label}>Editors:</span>
          <span className={styles.value}>
            {model.owners && model.owners.length > 0
              ? model.owners.map((owner) => fullNameWestern(owner)).join(', ')
              : '-'}
          </span>
        </div>
      </div>

      <Divider className={styles.divider} />

      {/* Technical Details */}
      <div className={styles.detailsGrid}>
        {externalModel && (
          <>
            <div className={styles.detailRow}>
              <span className={styles.label}>{metadataFieldLabels['platform']}:</span>
              <span className={styles.value}>
                {(model.metadata.platform === 'Pneumococcal Vaccines' ? 'PneuInsights' : model.metadata.platform) ||
                  '-'}
              </span>
            </div>

            <div className={styles.detailRow}>
              <span className={styles.label}>{metadataFieldLabels['label']}:</span>
              <span className={styles.value}>{model.metadata.label || '-'}</span>
            </div>
          </>
        )}
        {webModel && (
          <div className={styles.detailRow}>
            <span className={styles.label}>{metadataFieldLabels['platform']}:</span>
            <span className={styles.value}>{model.metadata.platform || '-'}</span>
          </div>
        )}

        <div className={styles.detailRow}>
          <span className={styles.label}>{metadataFieldLabels['type']}:</span>
          <span className={styles.value}>{getModelLanguageAndExecutionEnvironment(model.metadata) || '-'}</span>
        </div>

        {externalModel && (
          <div className={styles.detailRow}>
            <span className={styles.label}>Link:</span>
            <span className={styles.value}>
              {model.metadata.ext_url ? (
                <Button type="link" size="small">
                  <a href={model.metadata?.ext_url} target="_blank" rel="noopener noreferrer">
                    LINK
                  </a>
                </Button>
              ) : (
                '-'
              )}
            </span>
          </div>
        )}
      </div>

      {/* Request Access Button - Always show the component, it handles its own visibility logic */}
      <div style={{marginTop: '32px'}}>
        <ModelOverviewRequestAccess
          model={model}
          THModel={isModelTheorem(model)}
          displayAntDesign={true}
          width="100%"
          type="primary"
        />
      </div>
    </Card>
  );
};
